import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

const SupportCard = ({icon, title, children, action}) => (
    <Paper
        variant="outlined"
        sx={{
            height: "100%",
            p: {xs: 2.5, sm: 3},
            borderColor: "#E2E5EA",
            borderRadius: "16px",
            boxShadow: "0 12px 34px rgba(18, 28, 45, 0.05)",
        }}
    >
        <Box
            sx={{
                width: 42,
                height: 42,
                mb: 2,
                display: "grid",
                placeItems: "center",
                color: "#2B6BFD",
                backgroundColor: "#EEF4FF",
                borderRadius: "12px",
            }}
        >
            {icon}
        </Box>
        <Typography component="h2" sx={{fontSize: 20, fontWeight: 700, mb: 0.75}}>
            {title}
        </Typography>
        <Typography sx={{color: "#626A78", lineHeight: 1.65, mb: 2.25}}>
            {children}
        </Typography>
        {action}
    </Paper>
);

const SupportPage = () => {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = "Support | Libretto";
        return () => {
            document.title = previousTitle;
        };
    }, []);

    return (
        <Box sx={{minHeight: "100vh", backgroundColor: "#F7F8FB", color: "#16181D"}}>
            <Box
                component="header"
                sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderBottom: "1px solid #E7E9EE",
                }}
            >
                <Container
                    maxWidth="md"
                    sx={{height: 72, display: "flex", alignItems: "center", justifyContent: "space-between"}}
                >
                    <Box component={Link} to="/" sx={{display: "flex", alignItems: "center"}} aria-label="Libretto home">
                        <Box component="img" src="/v2-logo.svg" alt="Libretto" sx={{width: 106, height: "auto"}} />
                    </Box>
                    <Button component={Link} to="/dashboard" variant="outlined" sx={{borderRadius: "9px", px: 2}}>
                        Open Libretto
                    </Button>
                </Container>
            </Box>

            <Container component="main" maxWidth="md" sx={{py: {xs: 6, sm: 8}}}>
                <Box sx={{maxWidth: 680, mb: {xs: 4, sm: 5}}}>
                    <Typography
                        sx={{
                            mb: 1.25,
                            color: "#2B6BFD",
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                        }}
                    >
                        Libretto Support
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{fontSize: {xs: 38, sm: 52}, lineHeight: 1.08, fontWeight: 750, letterSpacing: "-0.035em"}}
                    >
                        How can we help?
                    </Typography>
                    <Typography sx={{mt: 2, color: "#626A78", fontSize: {xs: 18, sm: 20}, lineHeight: 1.55}}>
                        Get help with recording, editing, uploads, exports, your account, or importing Zoom cloud recordings.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))"},
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <SupportCard
                        icon={<EmailOutlinedIcon />}
                        title="Email support"
                        action={
                            <Button
                                component="a"
                                href="mailto:contact@libretto.fm?subject=Libretto%20Support"
                                variant="contained"
                                startIcon={<EmailOutlinedIcon />}
                                sx={{borderRadius: "9px", px: 2}}
                            >
                                contact@libretto.fm
                            </Button>
                        }
                    >
                        Tell us what you were trying to do and what happened. Include a screenshot or the exact error message when possible.
                    </SupportCard>

                    <SupportCard
                        icon={<AutoAwesomeOutlinedIcon />}
                        title="Ask the AI Assistant"
                        action={
                            <Button component={Link} to="/dashboard" variant="outlined" sx={{borderRadius: "9px", px: 2}}>
                                Open Libretto
                            </Button>
                        }
                    >
                        Signed-in users can use the in-app AI Assistant for quick questions about Libretto and their workflow.
                    </SupportCard>
                </Box>

                <Paper
                    component="section"
                    variant="outlined"
                    sx={{
                        mt: 2,
                        p: {xs: 2.5, sm: 4},
                        borderColor: "#E2E5EA",
                        borderRadius: "16px",
                        boxShadow: "0 12px 34px rgba(18, 28, 45, 0.05)",
                    }}
                >
                    <Typography component="h2" sx={{fontSize: 24, fontWeight: 700}}>
                        Importing recordings from Zoom
                    </Typography>
                    <Typography sx={{mt: 1, color: "#626A78", lineHeight: 1.65}}>
                        Libretto can import completed MP4 and M4A cloud recordings directly into a project.
                    </Typography>
                    <Button
                        component={Link}
                        to="/docs/zoom"
                        variant="outlined"
                        sx={{mt: 2, borderRadius: "9px", px: 2}}
                    >
                        View the complete Zoom guide
                    </Button>

                    <Box
                        component="ol"
                        sx={{mt: 2.5, mb: 0, pl: 2.75, color: "#303641", "& li": {pl: 0.75, mb: 1.25}}}
                    >
                        <li>Open a Libretto project and select <strong>Import from Zoom</strong>.</li>
                        <li>Connect your Zoom account and approve access when prompted.</li>
                        <li>Select the recording files you want, then choose <strong>Import</strong>.</li>
                    </Box>

                    <Box sx={{mt: 3, p: 2.25, backgroundColor: "#F5F8FF", borderRadius: "12px"}}>
                        <Typography sx={{fontWeight: 700, mb: 0.75}}>Not seeing a recording?</Typography>
                        <Typography sx={{color: "#626A78", lineHeight: 1.65}}>
                            Make sure it is a completed cloud recording and that you connected the Zoom account that owns it. If the problem continues, email us with the meeting topic and approximate date. Please never send us your password or access tokens.
                        </Typography>
                    </Box>

                    <Typography sx={{mt: 2.5, color: "#626A78", lineHeight: 1.65}}>
                        To disconnect Zoom, open <strong>Import from Zoom</strong> and select <strong>Disconnect</strong> beside the connection status.
                    </Typography>
                </Paper>
            </Container>

            <Box component="footer" sx={{borderTop: "1px solid #E7E9EE", backgroundColor: "#FFFFFF"}}>
                <Container
                    maxWidth="md"
                    sx={{
                        py: 3,
                        display: "flex",
                        flexDirection: {xs: "column", sm: "row"},
                        alignItems: {sm: "center"},
                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Typography sx={{color: "#7A818E", fontSize: 14}}>© Libretto</Typography>
                    <Box sx={{display: "flex", gap: 2.5}}>
                        <Typography component={Link} to="/privacy" sx={{color: "#626A78", fontSize: 14, textDecoration: "none"}}>
                            Privacy
                        </Typography>
                        <Typography component={Link} to="/terms" sx={{color: "#626A78", fontSize: 14, textDecoration: "none"}}>
                            Terms
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default SupportPage;
